import React, { useState, useRef, useEffect } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import Editor from "../../Components/textEditor";
import { formatDateForApi } from "../../Utils/timeutils";
import { buildStudentHref } from "../../Utils/utils";

export default function ClassDashboardContent({ selectedClass, selectedDate, selectedTimeUnit, autoSelectEnabled }) {
    const [dayData, setDayData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleDateChange = (date, dayData) => {
        setDayData(dayData);
        setLoading(false);
        setError(null);
    };

    const quillRef = useRef();

    const [todayText, setTodayText] = useState("");

    const CLASS_STATE_QUERY = gql`
      query ClassState($classUid: String!, $date: String!, $TimeUid: Int!) {
        classState(classUid: $classUid, date: $date, TimeUid: $TimeUid) {
          StudentsTotal
          StudentsAway { id username lastname surname }
          StudentsHere { id username lastname surname }
          ClassContent
        }
        schoolGroupById(id: $classUid) {
          __typename
          id
          name
        }
      }
    `;

    // Mutation to save editor content for a class on a given date
    const SET_DAY_CONTENT = gql`
      mutation SetDayContent($classId: String!, $date: String!, $content: String!) {
        setDayContent(classId: $classId, date: $date, content: $content)
      }
    `;

    const [setDayContentMutation] = useMutation(SET_DAY_CONTENT);

    // debounce timer for autosave
    const saveTimerRef = useRef(null);

    const formattedDate = formatDateForApi(selectedDate);

    const { data: classStateData, loading: classStateLoading, error: classStateError, refetch: refetchClassState } = useQuery(CLASS_STATE_QUERY, {
        variables: { classUid: selectedClass, date: formattedDate, TimeUid: selectedTimeUnit ?? 0 },
        skip: !selectedClass || !formattedDate || selectedTimeUnit == null
    });

    // Refetch when date, class or selected time unit changes
    useEffect(() => {
        if (selectedTimeUnit == null) return;
        if (refetchClassState) refetchClassState();
    }, [selectedTimeUnit, formattedDate, selectedClass, refetchClassState]);

    // initialize editor content from class state when it loads (or when selection changes)
    useEffect(() => {
        const contentString = classStateData?.classState?.ClassContent;
        // If there's no content for this date, clear the editor
        if (contentString == null || contentString === "") {
            const empty = [{ insert: "\n" }];
            setTodayText(empty);
            if (quillRef.current) {
                try { quillRef.current.setContents(empty); } catch (e) { /* ignore */ }
            }
            return;
        }

        try {
            const parsed = JSON.parse(contentString);
            setTodayText(parsed);
            if (quillRef.current) {
                try { quillRef.current.setContents(parsed); } catch (e) { /* ignore */ }
            }
        } catch (e) {
            // if content isn't JSON, set as plain text
            const plain = [{ insert: contentString }];
            setTodayText(plain);
            if (quillRef.current) {
                try { quillRef.current.setContents(plain); } catch (err) { /* ignore */ }
            }
        }
    }, [classStateData]);

    // handler for editor changes with autosave after user stops typing
    const handleEditorChange = (delta, oldDelta, source) => {
        if (source !== "user") return;
        const contents = quillRef.current?.getContents();
        if (!contents) return;
        setTodayText(contents);

        // restart debounce
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            // only save when we have a selected class and date
            if (!selectedClass || !formattedDate) return;
            try {
                const payload = JSON.stringify(contents);
                setDayContentMutation({ variables: { classId: selectedClass, date: formattedDate, content: payload } })
                    .catch(err => {
                        // swallow for now or could set an error state
                        console.error('Failed to save day content', err);
                    });
            } catch (e) {
                console.error('Failed to serialize editor contents', e);
            }
        }, 1500);
    };

    // cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, []);

    if (loading) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">Loading...</p>
            </section>
        );
    }

    if (error) {
        return (
            <section className="container mx-auto">
                <p className="text-red-600 mb-4">Error fetching data: {error.message}</p>
            </section>
        );
    }

    return (
        <section className="container mx-auto">
            <div className="my-12">
                <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="wrap group block w-full text-left bg-white rounded-xl border-2 border-neutral-200 transition-all duration-100">
                        <div className="p-4">
                            <h2 className="text-lg font-semibold mb-2 text-neutral-800">Schüler in der Klasse</h2>
                            <p className="text-3xl font-bold text-neutral-600">{classStateData?.classState?.StudentsTotal || 0}</p>
                        </div>
                    </div>
                    <div className="wrap group block w-full text-left bg-white rounded-xl border-2 border-neutral-200 transition-all duration-100">
                        <div className="p-4">
                            <h2 className="text-lg font-semibold mb-2 text-neutral-800">
                                Aufsicht
                            </h2>
                            <p className="text-2xl font-bold text-neutral-900">
                                <span className="text-3xl font-bold text-neutral-600">{classStateData?.classState?.StudentsHere?.length || 0}</span>
                                <span className="text-sm font-semibold text-slate-500 tracking-wider"> / </span>
                                <span className="text-sm font-bold text-slate-600">{classStateData?.classState?.StudentsHere?.length + (classStateData?.classState?.StudentsAway?.length || 0) || 0}</span>
                            </p>
                        </div>
                        <div className="hidden md:block p-4 rounded-b-xl border-t-2 border-neutral-200 min-h-[4rem]">
                            {classStateData?.classState?.StudentsHere && classStateData.classState.StudentsHere.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {classStateData.classState.StudentsHere.map(s => (
                                        <a key={s.id} href={buildStudentHref(s.id)} className="inline-flex items-center px-3 py-1 bg-neutral-100 text-sm text-neutral-800 rounded-full border border-neutral-100 hover:bg-neutral-200">
                                            {s.lastname && s.surname ? `${s.lastname.toUpperCase()} ${s.surname}` : s.lastname || s.surname || ""}
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-neutral-600">Keine</div>
                            )}
                        </div>
                    </div>
                    <div className="wrap group block w-full text-left bg-white rounded-xl border-2 border-neutral-200 transition-all duration-100">
                        <div className="p-4">
                            <h2 className="text-lg font-semibold mb-2 text-neutral-800">
                                Abwesend
                            </h2>
                            <p className="text-2xl font-bold text-neutral-900">
                                <span className="text-3xl font-bold text-neutral-600">{classStateData?.classState?.StudentsAway?.length || 0}</span>
                                <span className="text-sm font-semibold text-slate-500 tracking-wider"> / </span>
                                <span className="text-sm font-bold text-slate-600">{classStateData?.classState?.StudentsHere?.length + (classStateData?.classState?.StudentsAway?.length || 0) || 0}</span>
                            </p>
                        </div>
                        <div className="hidden md:block p-4 rounded-b-xl border-t-2 border-neutral-200">
                            {classStateData?.classState?.StudentsAway && classStateData.classState.StudentsAway.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {classStateData.classState.StudentsAway.map(s => (
                                        <a key={s.id} href={buildStudentHref(s.id)} className="inline-flex items-center px-3 py-1 bg-neutral-100 text-sm text-neutral-800 rounded-full border border-neutral-100 hover:bg-neutral-200">
                                            {s.lastname && s.surname ? `${s.lastname.toUpperCase()} ${s.surname}` : s.lastname || s.surname || ""}
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-neutral-600">Keine</div>
                            )}
                        </div>
                    </div>
                </section>
                <Editor
                    ref={quillRef}
                    readOnly={false}
                    defaultValue={todayText}
                    onTextChange={handleEditorChange}
                />
            </div>
        </section>
    );
}